import { bossHealth } from '../../game/bossHealth';
import type { World } from '../../sim/World';

/* ═══════════════════════════════════════════════════════════════════════════
   Le HUD en jeu — DÉLIBÉRÉMENT SANS FRAMEWORK (PLAN.md §3.7).

   Ce n'est pas une préférence de style. Le vrai piège de performance d'un jeu
   web n'est pas le choix du framework : c'est le PONT entre la boucle de rendu
   et l'interface. Écrire dans l'état d'un framework depuis `requestAnimationFrame`
   coûte, à lui seul, davantage que tout le reste du HUD réuni.

   Contrat, et chaque clause répond à une panne précise :

     · ≤ 28 NŒUDS DOM, tous créés au montage, jamais recréés. Un HUD qui
       reconstruit ses nœuds fait travailler le ramasse-miettes pendant le jeu.

     · 20 Hz pour les valeurs continues, IMMÉDIAT sur événement. Le score n'a
       pas besoin de 60 rafraîchissements par seconde ; la perte d'une vie, si.

     · DIRTY-CHECK systématique. Écrire `textContent` avec la valeur déjà
       présente déclenche quand même un travail de layout.

     · JAUGES PAR VARIABLE CSS + `transform: scaleX()`. Animer `width`
       déclencherait un reflow à chaque frame — c'est l'erreur la plus chère et
       la plus courante des HUD web.

     · `contain: strict` sur le conteneur : le navigateur sait alors qu'aucun
       changement interne ne peut affecter la mise en page extérieure.

   Budget mesuré : ≤ 0,4 ms CPU par frame.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Cadence de rafraîchissement des valeurs continues, en frames. À 60 fps,
 *  un rafraîchissement tous les 3 frames donne 20 Hz. */
const REFRESH_EVERY = 3;

interface Cell {
  readonly el: HTMLElement;
  last: string;
}

export class Hud {
  readonly root: HTMLElement;
  private readonly cells = new Map<string, Cell>();
  private readonly bars = new Map<string, HTMLElement>();
  private readonly barValues = new Map<string, number>();
  private frame = 0;
  private bossRoot: HTMLElement | null = null;
  private bossShown = false;
  /** Enveloppe de la jauge de bouclier, masquée tant qu'aucune capacité n'est
   *  débloquée (voir `update`). */
  private shieldWrap: HTMLElement | null = null;
  private shieldShown = false;
  private ultiWrap: HTMLElement | null = null;
  private ultiReady = false;
  private bannerEl: HTMLElement | null = null;
  private mounted = false;

  constructor(parent: HTMLElement = document.body) {
    const root = document.createElement('div');
    root.id = 'hud';
    root.setAttribute('aria-hidden', 'true');
    this.root = root;
    parent.appendChild(root);
    this.build();
    this.mounted = true;
  }

  /** Tous les nœuds sont créés ICI, une fois. */
  private build(): void {
    const mk = (cls: string, parent: HTMLElement): HTMLElement => {
      const e = document.createElement('div');
      e.className = cls;
      parent.appendChild(e);
      return e;
    };

    // ── coin haut-gauche : vies, bombes, score
    const tl = mk('hud-tl', this.root);
    this.cell('lives', mk('hud-lives', tl));
    this.cell('bombs', mk('hud-bombs', tl));

    /* ── Jauge de BOUCLIER ──────────────────────────────────────────────────
       Une barre, pas un texte : le bouclier se lit en vision périphérique
       pendant l'esquive, exactement comme la chaîne et l'XP — un chiffre de PV
       serait illisible là où il compte. Deux nœuds (enveloppe + barre), dans le
       budget de 28.

       Elle reste CACHÉE tant que le joueur n'a aucune capacité de bouclier
       (`shieldMax <= 0`, voir `update`) : un vaisseau de série ne doit pas voir
       une jauge vide et permanente pour une mécanique qu'il n'a pas débloquée.
       Masquée dès le montage pour éviter un éclair au premier affichage. */
    const shieldWrap = mk('hud-bar-wrap hud-shield-wrap', tl);
    shieldWrap.style.display = 'none';
    this.bar('shield', mk('hud-bar hud-shield', shieldWrap));
    this.shieldWrap = shieldWrap;
    // L'arme secondaire a une recharge : sans indicateur, le joueur clique dans
    // le vide et conclut que le clic droit ne fonctionne pas. Une compétence à
    // temps de recharge invisible n'est pas utilisée.
    this.cell('secondary', mk('hud-secondary', tl));
    /* ⚠️ Une JAUGE en plus du texte, et c'est un retour de jeu qui l'a imposée :
       « je n'ai aucun indicateur pour mon arme secondaire ». La cellule existait
       pourtant et se remplissait — un test le confirme. Elle était simplement
       illisible : onze pixels de texte empilés sous les vies, les bombes et le
       score, à lire pendant qu'on esquive.

       Une durée en secondes demande de LIRE ; une barre qui se remplit se
       comprend en vision périphérique, sans quitter des yeux le rideau de
       projectiles. C'est la même raison qui a fait choisir une barre pour l'XP
       et pour la progression du niveau. */
    const secWrap = mk('hud-bar-wrap hud-sec-wrap', tl);
    this.bar('secondary', mk('hud-bar hud-sec', secWrap));
    // Jauge d'ULTI — toujours visible (tout joueur en dispose). Une classe
    // `ready` s'allume quand elle est pleine : la touche E devient utile.
    const ultiWrap = mk('hud-bar-wrap hud-ulti-wrap', tl);
    this.bar('ulti', mk('hud-bar hud-ulti', ultiWrap));
    this.ultiWrap = ultiWrap;
    this.cell('score', mk('hud-score', tl));
    this.cell('mult', mk('hud-mult', tl));

    // ── barre de chaîne
    const chainWrap = mk('hud-bar-wrap', tl);
    this.bar('chain', mk('hud-bar hud-chain', chainWrap));

    // ── coin haut-droit : niveau et progression d'XP
    const tr = mk('hud-tr', this.root);
    this.cell('level', mk('hud-level', tr));
    const xpWrap = mk('hud-bar-wrap', tr);
    this.bar('xp', mk('hud-bar hud-xp', xpWrap));

    /* ── haut d'écran : la barre de BOSS ────────────────────────────────
       Large, centrée, au-dessus de tout. Un boss est l'événement que deux
       minutes de niveau préparent ; sans repère à l'écran, il ressemble à un
       gros ennemi et le joueur ne sait même pas qu'il en affronte un.

       ⚠️ Quatre nœuds, et le compte des parties tient dans le LIBELLÉ.

       La première version dessinait une rangée de six segments, un par partie.
       Plus lisible d'un coup d'œil — et refusée par le budget de 28 nœuds du
       HUD, qu'elle portait à 30. Le budget a eu raison : il défend le coût de
       mise en page d'un élément qui vit au-dessus d'un canvas animé, et le
       relever pour un confort d'affichage aurait été payer une frame pour une
       préférence.

       Le texte fait d'ailleurs mieux le travail pédagogique : « NOYAU BLINDÉ ·
       4 armes » dit à la fois pourquoi la barre ne descend pas et ce qu'il
       reste à démonter, là où six carrés laissaient deviner le lien. */
    const boss = mk('hud-boss', this.root);
    this.cell('boss-nom', mk('hud-boss-nom', boss));
    const bossWrap = mk('hud-bar-wrap hud-boss-wrap', boss);
    this.bar('boss', mk('hud-bar hud-boss-bar', bossWrap));
    this.bossRoot = boss;

    // ── centre : alerte de death-bomb. Le seul élément autorisé à recouvrir le
    //    terrain, parce qu'il ne dure que 150 ms et qu'il est vital.
    this.cell('alert', mk('hud-alert', this.root));

    /* ── centre-haut : bannière de passage de niveau ────────────────────────
       Un monde est découpé en plusieurs niveaux qui s'enchaînent SANS pause :
       sans repère, le joueur ne verrait pas qu'il a franchi un palier. La
       bannière est éphémère et son apparition/effacement est piloté par une
       animation CSS — la boucle de rendu n'y touche jamais après le déclenchement,
       et elle ne pèse donc pas sur le budget de 0,4 ms. */
    this.bannerEl = mk('hud-banner', this.root);

    // ── bas : progression du niveau
    const bottom = mk('hud-bottom', this.root);
    const progWrap = mk('hud-bar-wrap', bottom);
    this.bar('progress', mk('hud-bar hud-progress', progWrap));
  }

  private cell(key: string, el: HTMLElement): void {
    this.cells.set(key, { el, last: ' ' });
  }

  private bar(key: string, el: HTMLElement): void {
    this.bars.set(key, el);
    this.barValues.set(key, -1);
  }

  /** Écriture avec dirty-check. Sans elle, réécrire la même chaîne déclenche
   *  quand même un travail de mise en page. */
  private set(key: string, value: string): void {
    const c = this.cells.get(key);
    if (!c || c.last === value) return;
    c.el.textContent = value;
    c.last = value;
  }

  /** Jauge par variable CSS. ⚠️ JAMAIS `style.width` : cela déclenche un reflow
   *  à chaque frame. `transform: scaleX(var(--v))` est compositée par le GPU. */
  private setBar(key: string, ratio: number): void {
    const el = this.bars.get(key);
    if (!el) return;
    // Quantifié au centième : sous ce seuil, aucun pixel ne bouge à l'écran, et
    // écrire la variable coûterait pour rien.
    const q = Math.round(Math.max(0, Math.min(1, ratio)) * 100) / 100;
    if (this.barValues.get(key) === q) return;
    el.style.setProperty('--v', String(q));
    this.barValues.set(key, q);
  }

  /**
   * Une frame. `force` court-circuite la cadence de 20 Hz pour les événements
   * qui ne peuvent pas attendre : perte de vie, rupture de chaîne, fenêtre de
   * death-bomb.
   */
  update(w: World, level: number, xpRatio: number, levelProgress: number, force = false): void {
    if (!this.mounted) return;

    const p = w.player;

    /* La barre de boss est mise à jour SANS attendre la cadence de 20 Hz : la
       vie d'un boss chute par paliers francs quand une partie cède, et un
       retard de 50 ms sur ce retour rend le tir moins satisfaisant. Le
       dirty-check en dessous fait que ça ne coûte rien quand rien ne bouge. */
    const bh = bossHealth(w);
    if (this.bossRoot) {
      const visible = bh !== null;
      if (visible !== this.bossShown) {
        this.bossRoot.style.display = visible ? '' : 'none';
        this.bossShown = visible;
      }
      if (bh) {
        this.setBar('boss', bh.core);
        /* ⚠️ « NOYAU BLINDÉ » explique une barre qui ne bouge pas. Sans ce
           libellé, un joueur qui tire sur un noyau intouchable voit une jauge
           parfaitement figée et en conclut que son arme ne fait rien — alors
           que c'est la leçon du combat qu'il est en train de recevoir. */
        this.set(
          'boss-nom',
          bh.parts > 0
            ? `${bh.shielded ? 'NOYAU BLINDÉ' : 'NOYAU'} · ${bh.parts} arme${bh.parts > 1 ? 's' : ''}`
            : 'NOYAU EXPOSÉ',
        );
      }
    }

    // L'alerte de death-bomb est TOUJOURS immédiate : 150 ms de fenêtre ne
    // survivraient pas à un rafraîchissement à 20 Hz.
    const alert = p.deathBombT > 0 ? 'BOMBE !' : '';
    this.set('alert', alert);

    if (!force && this.frame++ % REFRESH_EVERY !== 0) return;

    this.set('lives', '◆'.repeat(Math.max(0, p.lives)) + '◇'.repeat(Math.max(0, 3 - p.lives)));
    this.set('bombs', '◉'.repeat(Math.max(0, p.bombs)));

    /* Bouclier : masqué tant qu'aucune capacité n'est débloquée, sinon la jauge
       se remplit au prorata des charges. La bascule d'affichage passe par le même
       dirty-check `shieldShown` que la barre de boss — écrire `style.display`
       chaque frame coûterait pour rien. `setBar` porte son propre dirty-check. */
    if (this.shieldWrap) {
      const smax = p.shieldMax;
      const on = smax > 0;
      if (on !== this.shieldShown) {
        this.shieldWrap.style.display = on ? '' : 'none';
        this.shieldShown = on;
      }
      if (on) this.setBar('shield', p.shield / smax);
    }

    /* Ulti : la jauge se remplit au combat. Une classe `ready` s'allume à plein
       (dirty-check dédié, comme le bouclier — pas d'écriture de classe par frame). */
    this.setBar('ulti', p.ulti);
    if (this.ultiWrap) {
      const ready = p.ulti >= 1;
      if (ready !== this.ultiReady) {
        this.ultiWrap.classList.toggle('ready', ready);
        this.ultiReady = ready;
      }
    }

    const sec = p.secondary >= 0 ? w.defs.secondaries[p.secondary] : undefined;
    /* Fraction de recharge écoulée : 1 = prête. On divise par la recharge
       NOMINALE de l'arme et non par un maximum observé — deux armes de recharges
       différentes doivent remplir leur jauge à des vitesses différentes, sinon
       la jauge ne dit plus rien du choix fait au briefing. */
    this.setBar('secondary', sec && sec.cooldown > 0 ? 1 - p.secondaryCd / sec.cooldown : 1);
    this.set(
      'secondary',
      sec === undefined
        ? ''
        : p.secondaryCd > 0
          ? `${sec.name} ${p.secondaryCd.toFixed(1)} s`
          : `${sec.name} ▸ PRÊT`,
    );
    this.set('score', Math.floor(w.score).toLocaleString('fr-FR'));
    this.set('mult', `×${(1 + Math.floor(w.kills / 4) * 0.1).toFixed(2)}`);
    this.set('level', `NV ${level}`);

    this.setBar('chain', w.grazeCount > 0 ? 1 : 0);
    this.setBar('xp', xpRatio);
    this.setBar('progress', levelProgress);
  }

  /**
   * Affiche brièvement une bannière de passage de niveau, puis la laisse
   * s'effacer seule. Appelée au franchissement d'un niveau-étape (à l'intérieur
   * d'un monde), là où il n'y a pas d'écran de cartes pour marquer le palier.
   *
   * ⚠️ L'animation est relancée en retirant puis rajoutant la classe, avec une
   * lecture de `offsetWidth` entre les deux pour forcer un reflow : sans elle,
   * le navigateur regrouperait les deux mutations et l'animation ne repartirait
   * pas si deux niveaux s'enchaînaient vite.
   */
  flashBanner(text: string): void {
    const el = this.bannerEl;
    if (!el) return;
    el.textContent = text;
    el.classList.remove('is-flash');
    void el.offsetWidth;
    el.classList.add('is-flash');
  }

  /** Nombre total de nœuds — surveillé en CI contre le budget de 28. */
  get nodeCount(): number {
    return this.root.querySelectorAll('*').length + 1;
  }

  show(): void {
    this.root.style.display = '';
  }
  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
    this.mounted = false;
  }
}

/** Budget du plan. Exporté pour que le test lise la même valeur que le code. */
export const HUD_NODE_BUDGET = 28;
